import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, tEnv, tCategory } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { CritBadge, StatusBadge, EmptyState } from "@/components/shared/Badges";
import CsvImportDialog from "@/pages/CsvImportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Server, Plus, Search, Pencil, Trash2, Upload } from "lucide-react";

const EMPTY = {
  name: "", category: "server", criticality: "medium", environment: "production",
  owner: "", ip_address: "", status: "active", description: "",
  technology: "", version: "", internet_exposed: false, logical_location: "", last_assessment: "",
};

export default function Assets() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: assets = [], isLoading } = useQuery({
    queryKey: ["assets", q],
    queryFn: async () => (await api.get("/assets", { params: q ? { q } : {} })).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["assets"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["graph"] });
  };

  const save = useMutation({
    mutationFn: async () =>
      editing
        ? (await api.put(`/assets/${editing}`, form)).data
        : (await api.post("/assets", form)).data,
    onSuccess: () => {
      toast.success(editing ? "Actif mis à jour" : "Actif créé");
      setOpen(false);
      invalidate();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/assets/${id}`),
    onSuccess: () => { toast.success("Actif supprimé"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({ name: a.name, ...EMPTY, ...a.attributes });
    setOpen(true);
  };

  const writable = can("asset:write");

  return (
    <AppShell
      title="Actifs"
      subtitle={`${assets.length} actif(s) — projection type=asset`}
      actions={writable && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => setImportOpen(true)} data-testid="import-assets-button">
            <Upload className="h-4 w-4" /> Import CSV
          </Button>
          <Button size="sm" className="rounded-sm gap-2" onClick={openCreate} data-testid="create-asset-button">
            <Plus className="h-4 w-4" /> Nouvel actif
          </Button>
        </div>
      )}
    >
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un actif…"
          className="pl-9 rounded-sm" data-testid="asset-search-input" />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground font-mono">Chargement…</p>
      ) : assets.length === 0 ? (
        <EmptyState icon={Server} title="Aucun actif" hint="Créez votre premier actif ou importez un CSV."
          action={writable && <Button size="sm" className="rounded-sm gap-2" onClick={openCreate}><Plus className="h-4 w-4" /> Nouvel actif</Button>} />
      ) : (
        <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="assets-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead>Nom</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Criticité</TableHead>
                <TableHead>Environnement</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assets.map((a) => (
                <TableRow key={a.id} className="border-border cursor-pointer hover:bg-secondary/40"
                  onClick={() => navigate(`/assets/${a.id}`)} data-testid={`asset-row-${a.id}`}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">{tCategory(a.attributes.category)}</TableCell>
                  <TableCell><CritBadge value={a.attributes.criticality} /></TableCell>
                  <TableCell className="text-muted-foreground text-xs">{tEnv(a.attributes.environment)}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{a.attributes.ip_address || "—"}</TableCell>
                  <TableCell><StatusBadge value={a.attributes.status} /></TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {writable && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)} data-testid={`edit-asset-${a.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300"
                          onClick={() => { if (window.confirm("Supprimer cet actif ?")) del.mutate(a.id); }}
                          data-testid={`delete-asset-${a.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="asset-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">{editing ? "Modifier l'actif" : "Nouvel actif"}</DialogTitle>
            <DialogDescription>Renseignez les attributs de l'actif (projection type=asset).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label className="text-xs">Nom</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 rounded-sm" data-testid="asset-name-input" />
            </div>
            <div>
              <Label className="text-xs">Catégorie</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs">Criticité</Label>
              <Select value={form.criticality} onValueChange={(v) => setForm({ ...form, criticality: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="asset-criticality-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critique</SelectItem>
                  <SelectItem value="high">Élevé</SelectItem>
                  <SelectItem value="medium">Moyen</SelectItem>
                  <SelectItem value="low">Faible</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Environnement</Label>
              <Select value={form.environment} onValueChange={(v) => setForm({ ...form, environment: v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="staging">Préproduction</SelectItem>
                  <SelectItem value="development">Développement</SelectItem>
                  <SelectItem value="dr">Reprise (PRA)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="degraded">Dégradé</SelectItem>
                  <SelectItem value="offline">Hors ligne</SelectItem>
                  <SelectItem value="planned">Planifié</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Propriétaire</Label>
              <Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs">Adresse IP</Label>
              <Input value={form.ip_address} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs">Technologie</Label>
              <Input value={form.technology} onChange={(e) => setForm({ ...form, technology: e.target.value })} className="mt-1 rounded-sm" data-testid="asset-technology-input" />
            </div>
            <div>
              <Label className="text-xs">Version</Label>
              <Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs">Exposition Internet</Label>
              <Select value={String(form.internet_exposed)} onValueChange={(v) => setForm({ ...form, internet_exposed: v === "true" })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="asset-exposure-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">Non exposé</SelectItem>
                  <SelectItem value="true">Exposé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Localisation logique</Label>
              <Input value={form.logical_location} onChange={(e) => setForm({ ...form, logical_location: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs">Dernière évaluation</Label>
              <Input type="date" value={form.last_assessment} onChange={(e) => setForm({ ...form, last_assessment: e.target.value })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" onClick={() => save.mutate()} disabled={!form.name || save.isPending} data-testid="save-asset-button">
              {save.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CsvImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={invalidate} />
    </AppShell>
  );
}
